import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AccountPrefsModal } from '@/features/auth/AccountPrefsModal';
import { can } from '@/lib/auth/access';
import { hasKeycloakSession, logoutFromKeycloak } from '@/lib/auth/keycloak';
import { buildInfoText } from '@/lib/buildInfo';
import { type DataSource, config, setDataSource } from '@/lib/config';
import { useAuthStore, useCurrentRole } from '@/lib/store/auth';
import { useUiStore } from '@/lib/store/ui';
import { RELEASED, VERSION_LABEL } from '@/lib/version';
import { Building2, Copy, Database, LogOut, Settings, User } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/** Real Keycloak sessions must end via `signoutRedirect()` — a bare
 *  `setUser(null)` only clears our local state, leaving the Keycloak SSO
 *  cookie live, so the next "Sign in with..." click would silently
 *  re-authenticate against the still-active session with no prompt.
 *  Falls back to the plain local clear for mock/demo sessions, which have
 *  no server-side session to end. */
async function signOut(setUser: (u: null) => void): Promise<void> {
  if (await hasKeycloakSession()) {
    await logoutFromKeycloak();
    return; // signoutRedirect navigates away; userUnloaded clears the store.
  }
  setUser(null);
}

export function ProfileMenu() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const role = useCurrentRole();
  const openTenantSettings = useUiStore((s) => s.setTenantSettingsOpen);
  const setLoginOpen = useUiStore((s) => s.setLoginOpen);
  const [prefsOpen, setPrefsOpen] = useState(false);
  // Target value the confirm dialog will commit to when the user clicks
  // "Switch". Setting it opens the dialog; clearing closes it.
  const [pendingSource, setPendingSource] = useState<DataSource | null>(null);

  if (!user) {
    return (
      <>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLoginOpen(true)}
            aria-label="Sign in"
          >
            <User size={13} /> <span className="hidden sm:inline">Sign in</span>
          </Button>
          {/* Signed-out users still need to flip the data source — otherwise
              switching to live (which skips bootstrapMockAuth) traps them with
              no toggle. Render a minimal dropdown holding just the toggle. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Data source settings">
                <Database size={13} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DataSourceToggle onChoose={setPendingSource} />
              <DropdownMenuSeparator />
              <BuildFooter />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <DataSourceConfirmDialog pending={pendingSource} onClose={() => setPendingSource(null)} />
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="rounded-full relative">
            <span className="text-[0.769em] font-bold">{user.initials}</span>
            <span className="absolute -bottom-0.5 -right-0.5 size-2 rounded-full bg-(--color-green) ring-2 ring-(--color-surface)" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>Tenant</DropdownMenuLabel>
          <div className="px-2 pb-2">
            <div className="text-[0.846em] font-bold">{config.tenant.label}</div>
            <div className="text-[0.615em] font-mono text-(--color-text3)">
              {config.tenant.id}.flysafe.live · Online
            </div>
          </div>
          <DropdownMenuSeparator />
          <div className="px-2 py-2 flex items-center gap-2">
            <div
              className="size-9 rounded-full text-white flex items-center justify-center font-bold text-xs"
              style={{ background: 'linear-gradient(135deg,#1d5ea8,#0f3a6e)' }}
            >
              {user.initials}
            </div>
            <div className="min-w-0">
              <div className="text-[0.846em] font-semibold truncate">{user.fullName}</div>
              <div className="text-[0.692em] text-(--color-text3) truncate">{user.email}</div>
            </div>
          </div>
          <DropdownMenuSeparator />
          <div className="px-2 py-2">
            <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) mb-1">
              Roles
            </div>
            <div className="flex flex-wrap gap-1">
              {user.roles.map((r) => (
                <span
                  key={r}
                  className="text-[0.615em] font-semibold px-1.5 py-0.5 rounded bg-(--color-green-bg) text-(--color-green)"
                >
                  {r}
                </span>
              ))}
            </div>
          </div>
          <DropdownMenuSeparator />
          <DataSourceToggle onChoose={setPendingSource} />
          <DropdownMenuSeparator />
          {/* Tenant partnership management is admin-only — 86e22kunv / 86e232yn7. */}
          {can('tenant:manage', role) && (
            <DropdownMenuItem onClick={() => openTenantSettings(true)}>
              <Building2 size={13} /> Tenant Settings
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setPrefsOpen(true)}>
            <Settings size={13} /> Account Settings
          </DropdownMenuItem>
          <DropdownMenuItem danger onClick={() => void signOut(setUser)}>
            <LogOut size={13} /> Sign Out
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <BuildFooter />
        </DropdownMenuContent>
      </DropdownMenu>
      <AccountPrefsModal open={prefsOpen} onOpenChange={setPrefsOpen} />
      <DataSourceConfirmDialog pending={pendingSource} onClose={() => setPendingSource(null)} />
    </>
  );
}

/** Build stamp at the foot of both dropdowns. Clicking copies the full
 *  diagnostics block (version, tenant, data source, UA) so a bug report
 *  carries everything support needs in one paste.
 *
 *  A plain <button>, not a DropdownMenuItem — an item would close the menu on
 *  select, hiding the version the moment the user reaches for it. */
function BuildFooter() {
  const copy = async () => {
    const text = buildInfoText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Build info copied');
    } catch {
      // Clipboard API is unavailable on insecure origins — fall back to the
      // console so the info is still recoverable.
      console.warn('[build] clipboard unavailable\n', text);
      toast.error('Copy failed — build info logged to console');
    }
  };
  return (
    <button
      type="button"
      onClick={() => void copy()}
      title="Copy build info"
      className="w-full px-2 py-1.5 flex items-center justify-between gap-2 text-[0.615em] font-mono text-(--color-text3) hover:text-(--color-text2) transition-colors"
    >
      <span>
        {VERSION_LABEL} · {RELEASED}
      </span>
      <Copy size={10} className="shrink-0" />
    </button>
  );
}

/** Toggle row used inside both the signed-in and signed-out dropdowns.
 *  Calling `onChoose(next)` opens the confirmation dialog up the tree. */
function DataSourceToggle({ onChoose }: { onChoose: (s: DataSource) => void }) {
  return (
    <div className="px-2 py-2">
      <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) mb-1 flex items-center gap-1">
        <Database size={10} /> Data Source
      </div>
      <div className="flex gap-1.5">
        <Button
          size="xs"
          variant="toggle"
          data-on={config.dataSource === 'mock'}
          onClick={() => {
            if (config.dataSource !== 'mock') onChoose('mock');
          }}
          className="flex-1"
        >
          Demo
        </Button>
        <Button
          size="xs"
          variant="toggle"
          data-on={config.dataSource === 'live'}
          onClick={() => {
            if (config.dataSource !== 'live') onChoose('live');
          }}
          className="flex-1"
        >
          Live
        </Button>
      </div>
      <div className="text-[0.615em] text-(--color-text3) mt-1.5 leading-snug">
        Switching reloads the app.
      </div>
    </div>
  );
}

function DataSourceConfirmDialog({
  pending,
  onClose,
}: {
  pending: DataSource | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={pending !== null}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Switch data source?</DialogTitle>
          <DialogDescription>
            {pending === 'live'
              ? 'The app will reload and connect to live data. Fields not yet wired up on the backend will show — until each topic ships.'
              : 'The app will reload and use demo data. Any live connections will be torn down.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (pending) setDataSource(pending);
            }}
          >
            Switch to {pending === 'live' ? 'Live' : 'Demo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
